import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { COGNITIVE_DISTORTIONS } from '@/constants/cognitiveDistortions';
import { Brain } from 'lucide-react';

interface DistortionCount {
  distortionId: string;
  count: number;
}

interface CognitiveDistortionsPieChartProps {
  distortionCounts: DistortionCount[];
  totalThoughts: number;
}

export function CognitiveDistortionsPieChart({ 
  distortionCounts, 
  totalThoughts 
}: CognitiveDistortionsPieChartProps) {
  // Transform data for the chart
  const chartData = distortionCounts
    .filter(d => d.count > 0)
    .map(d => {
      const distortion = COGNITIVE_DISTORTIONS.find(cd => cd.id === d.distortionId);
      return {
        name: distortion?.name || d.distortionId,
        value: d.count,
        percentage: Math.round((d.count / totalThoughts) * 100),
        colour: distortion?.colour || 'hsl(var(--muted))'
      };
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 6); // Top 6 for readability

  if (chartData.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Brain className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Cognitive Patterns</CardTitle>
              <CardDescription>Track your thinking patterns over time</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Brain className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">
              Start logging thoughts to see your cognitive patterns
            </p>
            <p className="text-sm text-muted-foreground/70 mt-2">
              Understanding your patterns is the first step to changing them
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background/95 backdrop-blur-sm border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-sm">{data.name}</p>
          <p className="text-muted-foreground text-xs">
            {data.value} thought{data.value !== 1 ? 's' : ''} ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-primary/10">
            <Brain className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Cognitive Patterns</CardTitle>
            <CardDescription>
              Your most common thinking patterns from {totalThoughts} logged thought{totalThoughts !== 1 ? 's' : ''}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percentage }) => `${percentage}%`}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.colour}
                    className="stroke-background stroke-2"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                layout="vertical" 
                align="right" 
                verticalAlign="middle"
                formatter={(value) => (
                  <span className="text-xs text-foreground">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-muted-foreground text-center">
            Recognising patterns is the first step to challenging unhelpful thoughts
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
